import { Field, defineStyle } from '@chakra-ui/react'
import {
	DropdownIndicatorProps,
	Select,
	SelectInstance,
	components
} from 'chakra-react-select'
import cn from 'clsx'
import { forwardRef, useMemo, useState } from 'react'

import ArrowDown from '@/assets/icons/arrow-down-2.svg'

import { ILanguage } from '@/types/task.interface'

interface ICustomSelectLanguageProps {
	fieldLabel?: string
	value: ILanguage | null
	onChange: (value: ILanguage | null) => void
	options: ILanguage[]
	rootClassname?: string
	inputClassname?: string
	placeholder?: string
}

const CustomSelectLanguage = forwardRef<
	SelectInstance,
	ICustomSelectLanguageProps
>(
	(
		{
			fieldLabel,
			value,
			onChange,
			options,
			rootClassname,
			inputClassname,
			placeholder
		},
		ref
	) => {
		const [isFocused, setIsFocused] = useState(false)

		const hasValueOrFocus = value || isFocused
		const hasValueNoFocus = value && !isFocused

		const selectOptions = useMemo(
			() =>
				options.map(lang => ({
					label: lang.language,
					value: lang.code
				})),
			[options]
		)

		const selectedOption = useMemo(() => {
			if (!value) return null
			return {
				label: value.language,
				value: value.code
			}
		}, [value])

		const handleChange = (option: { label: string; value: string } | null) => {
			const selected = options.find(lang => lang.code === option?.value) || null
			onChange(selected)
		}

		const DropdownIndicator = (
			props: DropdownIndicatorProps<{ label: string; value: string }>
		) => {
			return (
				<components.DropdownIndicator {...props}>
					<ArrowDown className='w-3 h-[8.5px]' />
				</components.DropdownIndicator>
			)
		}

		return (
			<Field.Root className={rootClassname}>
				<div className='w-full relative'>
					<Select
						ref={ref}
						components={{
							DropdownIndicator
						}}
						className={cn(
							'peer w-full rounded-[8px] h-[60px] focus:border-black bg-gray-300 z-20',
							hasValueOrFocus && 'has-value',
							hasValueNoFocus && 'value-nofocus',
							inputClassname
						)}
						placeholder={placeholder || ''}
						isClearable={isFocused}
						options={selectOptions}
						value={selectedOption}
						onChange={handleChange}
						onFocus={() => setIsFocused(true)}
						onBlur={() => setIsFocused(false)}
						chakraStyles={{
							control: (base, state) => ({
								...base,
								borderWidth: '2px',
								borderColor: state.isFocused ? 'black' : 'transparent',
								borderRadius: state.isFocused ? '8px' : '0',
								minH: '60px',
								w: '100%',
								paddingX: '14px'
							}),
							singleValue: base => ({
								...base,
								pl: '2px',
								fontSize: '16px',
								lineHeight: '28px',
								fontWeight: '600'
							}),
							menuList: base => ({
								...base,
								width: '101%',
								left: '-2px',
								fontSize: '16px'
							})
						}}
					/>
					<Field.Label
						css={floatingStyles}
						style={{ background: 'none' }}
						className={cn({
							'has-value': hasValueOrFocus,
							'value-nofocus': hasValueNoFocus
						})}
					>
						{fieldLabel}
					</Field.Label>
				</div>
			</Field.Root>
		)
	}
)

const floatingStyles = defineStyle({
	pos: 'absolute',
	bg: 'bg',
	px: '18px',
	top: '2.5',
	color: 'fg.muted',
	fontWeight: 'normal',
	pointerEvents: 'none',
	transition: 'top 0.2s ease, color 0.2s ease',

	'&.has-value': {
		top: '0',
		color: 'fg'
	},
	'&.value-nofocus': {
		color: 'fg.muted'
	}
})

export default CustomSelectLanguage
